/**
 * Parses studio-specific directive tags out of Claude's streaming text.
 * Mirrors the `[POINT:...]` parser pattern in
 * `src/pointing/PointingTagParser.ts` so behavior is consistent.
 *
 * Currently the only studio directive is panel navigation:
 *
 *   [OPEN:panel_id]
 *
 * It can appear anywhere in the response (Claude's choice), but the
 * recommended placement is at the very end so streaming doesn't show
 * a panel-switch flash before the user has read the message.
 *
 * Multiple OPEN tags in one response are tolerated — each fires its
 * own callback, and only the LAST one wins for which panel is
 * actually visible (PanelManager.open is idempotent).
 */

import type { StudioPanelId } from "./PanelManager";
import { STUDIO_PANEL_IDS } from "./PanelManager";

const COMPLETED_OPEN_TAG_REGEX = /\[OPEN:([a-z_]+)\]/gi;
const PARTIAL_OPEN_TAG_AT_END_REGEX = /\[OPEN:[^\]]*$/;

/** Set of valid panel ids — used to validate parsed tags. */
const VALID_PANEL_IDS = new Set<string>(STUDIO_PANEL_IDS);

export interface ParsedDirectives {
  /** Display text with all OPEN tags removed. */
  cleanedText: string;
  /** Ordered list of panel ids requested by the response. */
  panelIdsToOpen: StudioPanelId[];
}

/**
 * Strip any complete or partial `[OPEN:...]` substring from the
 * streaming text. Used on every chunk so the bubble never shows raw
 * tag metadata. Does NOT fire side effects — that happens once on the
 * final response via `parseAndExtractDirectives`.
 */
export function stripDirectivesFromStreamingText(text: string): string {
  let cleaned = text.replace(COMPLETED_OPEN_TAG_REGEX, "").trim();
  cleaned = cleaned.replace(PARTIAL_OPEN_TAG_AT_END_REGEX, "");
  return cleaned.replace(/\s+$/, "");
}

/**
 * Parse the final response text. Returns the cleaned display text
 * (tags removed) plus the list of panel ids the response requested.
 * Unknown panel ids are dropped with a warn — Claude occasionally
 * invents panel names that don't exist.
 */
export function parseAndExtractDirectives(text: string): ParsedDirectives {
  const panelIdsToOpen: StudioPanelId[] = [];
  const cleanedText = text
    .replace(COMPLETED_OPEN_TAG_REGEX, (_match, capturedId: string) => {
      const normalizedId = capturedId.toLowerCase();
      if (VALID_PANEL_IDS.has(normalizedId)) {
        panelIdsToOpen.push(normalizedId as StudioPanelId);
      } else {
        console.warn(`[studio] OPEN tag with unknown panel id: ${capturedId}`);
      }
      return "";
    })
    .replace(/\s+/g, " ")
    .trim();
  return { cleanedText, panelIdsToOpen };
}
